'use client';
import { useCallback } from 'react';
import { useToast } from '../components/ui/Toast';
import { copyText } from './clipboard';
import { useTranslation } from './i18n';

export interface CopyActionOptions {
  /** Identifies the thing being copied, so copying it again updates its card instead of stacking one. */
  key?: string;
  /** Curated, localized success text. Never the copied value: it may be a secret. */
  message?: string;
}

/** The one clipboard boundary. The returned function takes the text at click time (a fixed token and a
 * selected tree path are the same call), awaits the real write, and reports exactly one outcome through the
 * toast seam: a success, or `copyFailed` when the browser refuses. There is no "copied" state, label or
 * timer for a control to keep. Resolves to whether the write happened. */
export function useCopyAction(): (text: string, options?: CopyActionOptions) => Promise<boolean> {
  const { t } = useTranslation();
  const { toast } = useToast();
  return useCallback(async (text, options = {}) => {
    const ok = await copyText(text);
    if (ok) toast(options.message ?? t.common.copiedToast, 'ok', options.key ? { key: options.key } : {});
    else toast(t.common.copyFailed, 'error', options.key ? { key: options.key } : {});
    return ok;
  }, [t, toast]);
}
